/**
 * Best-times presentation rules — the two decisions that turn a served (UTC day, UTC hour) pair into a row a
 * band can act on. Pure and in their own module for the reason `trendingCopy.ts` is: they are the ONE place
 * these rules live, they are worth testing without mounting a screen, and a component file that also exports
 * helpers loses fast refresh.
 */

/** Confidence is RANK, not data — top two high, next two mid, the rest low. The server sends no confidence
 *  field and deliberately never will: a row's rank is already its position in this list, so shipping the tier
 *  as data would be a value nothing computes. Matches legacy (`i < 2 ? hi : i < 4 ? mid : low`) and the UAT's
 *  "hi/mid/low by rank" (D-CONT-06.1). */
export function confidenceTier(rank: number): "high" | "mid" | "low" {
  return rank < 2 ? "high" : rank < 4 ? "mid" : "low";
}

/** The tier as a WORD, for the dot's accessible label — colour alone can never carry it (WCAG 1.4.1). */
export const TIER_WORD = { high: "high", mid: "medium", low: "low" } as const;

/**
 * Render a (UTC day-of-week, UTC hour) instant-of-week in the READER's local clock — "Tuesday" + "9pm".
 *
 * The server buckets in UTC and sends the pair unrendered precisely so this conversion can happen here, on the
 * clock of whoever has to act on the advice. Localizing can move the DAY as well as the hour (Monday 01:00 UTC
 * is Sunday evening in New York), which is why the day is derived from the converted instant rather than passed
 * through. `refDate` exists for tests — the actual date is irrelevant to the result beyond which DST period it
 * lands in, so a caller pinning it gets a deterministic answer.
 */
export function localizeBestTime(
  dayOfWeek: number,
  utcHour: number,
  opts: { refDate?: Date; timeZone?: string } = {},
): { day: string; time: string } {
  const ref = opts.refDate ?? new Date();
  // Walk from a reference day to the next date whose UTC weekday matches, then stamp the hour. Millisecond
  // arithmetic on a UTC epoch, so no month/year rollover to get wrong.
  const base = Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate());
  const shift = (dayOfWeek - new Date(base).getUTCDay() + 7) % 7;
  const at = new Date(base + shift * 86_400_000 + utcHour * 3_600_000);

  // Intl rather than getHours(), so `timeZone` can be pinned in a test — and so the weekday is named in the
  // reader's own locale. hour12 is forced: the UAT fails any 24-hour value outright (D-CONT-06.2), and a
  // reader whose locale defaults to 24h would otherwise see "19".
  const parts = new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    hour: "numeric",
    hour12: true,
    ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
  }).formatToParts(at);
  const pick = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";

  return {
    day: pick("weekday"),
    // "9pm" — the dayPeriod stripped to letters so an "a.m."-style locale still renders one token.
    time: `${pick("hour")}${pick("dayPeriod").toLowerCase().replace(/[^a-z]/g, "")}`,
  };
}
